import { faUser } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Heart,
  Search,
  ShoppingCart,
  ShoppingCartIcon,
  User,
  X,
} from 'lucide-react';
import { useContext, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import logo from '../../assets/brand/logo1.png';
import CartContext from '../../contexts/CartContext';
import UserContext from '../../contexts/UserContext';
import apiClient from '../../utils/api-client';
import './Navbar.css';
import NavLinks from './NavLinks';
import OffcanvasMenu from './OffcanvasMenu';
import Topbar from './Topbar';
const Navbar = () => {
  const { user } = useContext(UserContext);
  const { cart, wishList = [] } = useContext(CartContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [show, setShow] = useState(false);

  const navigate = useNavigate();
  const cartCount = cart?.length || 0;
  const wishListCount = wishList?.length || 0;
  const handleSearch = async (event) => {
    event.preventDefault();
    try {
      const response = await apiClient.get(
        `/search/products?product_name=${searchQuery}`
      );
      const searchData = response.data.data;
      setSearchQuery('');
      navigate('/search-results/', { state: { searchResults: searchData } });
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
  };
  const handleInputChange = (event) => {
    setSearchQuery(event.target.value);
  };
  const [isFocused, setIsFocused] = useState(false);

  const handleClear = () => {
    handleInputChange({ target: { value: '' } });
  };
  const handleNavLinkClick = () => {
    setShow(false);
  };
  return (
    <section>
      <Topbar />
      <div className="container-fluid">
        <nav
          className="col-sm-12 navbar navbar-expand-lg navbar-light bg-light"
          aria-label="Offcanvas navbar"
        >
          {/* LEFT SECTION - Offcanvas + Logo + Mobile Cart/Wishlist */}
          <div className="col-12 col-lg-3 d-flex align-items-center justify-content-between gap-2 mb-1">
            {/* Offcanvas Menu (Mobile) */}
            <div className="d-lg-none">
              <OffcanvasMenu user={user} cartCount={cartCount} />
            </div>

            {/* Brand Logo */}
            <div className="flex-grow-1 text-center text-lg-start">
              <Link
                to="/"
                className="navbar-brand m-0 d-flex align-items-center justify-content-center justify-content-lg-start"
              >
                <img src={logo} alt="Logo" className="brand-logo img-fluid " />
              </Link>
            </div>

            {/* Mobile Cart & Wishlist / Login */}
            <div className="d-lg-none d-flex align-items-center gap-3">
              <ul className="list-group list-group-horizontal mb-0 align-items-center">
                {/* Wishlist Icon */}
                <li className="list-group-item border-0 p-0 me-2 position-relative">
                  <NavLink
                    to="/wishlist"
                    onClick={handleNavLinkClick}
                    className="text-dark d-flex align-items-center justify-content-center"
                  >
                    <Heart size={24} className="icon" />
                    {wishListCount > 0 && (
                      <span className="badge rounded-circle position-absolute top-0 start-100 translate-middle">
                        {wishListCount}
                      </span>
                    )}
                  </NavLink>
                </li>

                {/* Cart Icon */}
                <li className="list-group-item border-0 p-0 position-relative">
                  <NavLink
                    to="/cart"
                    onClick={handleNavLinkClick}
                    className="text-dark d-flex align-items-center justify-content-center"
                  >
                    <ShoppingCart size={24} className="icon" />
                    {cartCount > 0 && (
                      <span className="badge rounded-circle position-absolute top-0 start-100 translate-middle cart-badge">
                        {cartCount}
                      </span>
                    )}
                  </NavLink>
                </li>
              </ul>

              {/* Login Button */}
              <NavLink
                to="/login"
                className="btn btn-sm btn-login d-flex align-items-center gap-2"
              >
                <FontAwesomeIcon icon={faUser} />
                <span>Login</span>
              </NavLink>
            </div>
          </div>

          {/* MIDDLE SECTION - Search Bar */}
          <div className="navbar-search  col-lg-6 col-md-12 col-sm-12 d-flex flex-grow-1 w-25">
            <form className="pro-search-form" onSubmit={handleSearch}>
              <div
                className={`pro-search-wrapper ${isFocused ? 'focused' : ''}`}
              >
                <Search className="search-icon" size={20} />
                <input
                  ref={(input) => input}
                  type="text"
                  className="pro-search-input"
                  placeholder="I'm looking for..."
                  value={searchQuery}
                  onChange={handleInputChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  required
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="clear-button"
                    onClick={handleClear}
                  >
                    <X size={16} />
                  </button>
                )}
                <button type="submit" className="pro-search-button">
                  Search
                </button>
              </div>
            </form>
          </div>

          {/* RIGHT SECTION - Cart, Wishlist, Account */}
          <div className="align_center col-lg-3 col-md-4 d-none d-lg-flex justify-content-end gap-4">
            {/* Cart */}
            <Link to="/cart" className="text-decoration-none position-relative">
              <ShoppingCartIcon size={26} strokeWidth={2} className="icon" />
              {cartCount > 0 && (
                <sup
                  id="total"
                  className="fw-bold text-purple translate-middle"
                  style={{ position: 'absolute', top: 0, left: '55%' }}
                >
                  {cartCount}
                </sup>
              )}
            </Link>

            {/* Wishlist */}
            <Link to="/wishlist" className="text-decoration-none">
              <Heart size={26} strokeWidth={2} className="icon" />
            </Link>

            {/* Login / Account */}
            {!user ? (
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  `btn-custom-purple ${isActive ? 'active' : ''}`
                }
              >
                <User size={16} /> Login
              </NavLink>
            ) : (
              <NavLink
                to="/account/profile"
                className={({ isActive }) =>
                  `btn-custom-purple ${isActive ? 'active' : ''}`
                }
              >
                <User size={16} /> Profile
              </NavLink>
            )}
          </div>
        </nav>
      </div>

      <NavLinks />
    </section>
  );
};
export default Navbar;
